"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const DEFAULT_ADV_IMAGE = "/image/new-version/molding/overmolding/advantages-of-overmolding.webp";
const DEFAULT_APP_IMAGE = "/image/new-version/molding/overmolding/applications-of-overmolding.webp";

const ADV_ITEM_KEYS = [
	{ titleKey: "adv1Title", descKey: "adv1Desc" },
	{ titleKey: "adv2Title", descKey: "adv2Desc" },
	{ titleKey: "adv3Title", descKey: "adv3Desc" },
	{ titleKey: "adv4Title", descKey: "adv4Desc" },
	{ titleKey: "adv5Title", descKey: "adv5Desc" },
] as const;

const APP_ITEM_KEYS = [
	{ titleKey: "app1Title", descKey: "app1Desc" },
	{ titleKey: "app2Title", descKey: "app2Desc" },
	{ titleKey: "app3Title", descKey: "app3Desc" },
	{ titleKey: "app4Title", descKey: "app4Desc" },
	{ titleKey: "app5Title", descKey: "app5Desc" },
] as const;

function CheckBullet() {
	return (
		<div
			className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-themePrimary400 sm:h-6 sm:w-6"
			aria-hidden
		>
			<svg className="h-3 w-3 text-white sm:h-3.5 sm:w-3.5" viewBox="0 0 12 10" fill="none" aria-hidden>
				<path
					d="M1 5l3.5 3.5L11 1"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</div>
	);
}

export interface OvermoldingAdvantagesApplicationsProps {
	translationNamespace?: string;
	advantagesImageSrc?: string;
	applicationsImageSrc?: string;
}

/**
 * 包覆成型：上块左图右文、下块左文右图；lg 下图与文案列等高（顶齐标题、底齐列表末），无圆角/阴影/描边
 */
export default function OvermoldingAdvantagesApplications({
	translationNamespace = "overmoldingAdvantagesApplicationsSection",
	advantagesImageSrc = DEFAULT_ADV_IMAGE,
	applicationsImageSrc = DEFAULT_APP_IMAGE,
}: OvermoldingAdvantagesApplicationsProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* Advantages：大屏左图右文；lg 双列 stretch，图用 absolute 铺满与右侧文案同高 */}
				<div className="mb-16 flex flex-col gap-8 md:mb-20 lg:flex-row lg:items-stretch lg:gap-12 xl:gap-16">
					<div className="relative w-full overflow-hidden lg:min-h-0 lg:w-1/2">
						<div className="relative aspect-[4/3] w-full lg:absolute lg:inset-0 lg:aspect-auto">
							<Image
								src={advantagesImageSrc}
								alt={t("advantagesImageAlt")}
								fill
								className="object-fill"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>
					<div className="flex min-w-0 flex-col lg:w-1/2">
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("advantagesTitle")}
						</h2>
						<p className="mb-8 text-sm leading-relaxed text-black sm:mb-10 md:text-base">
							{t("advantagesIntro")}
						</p>
						<ul className="space-y-6 sm:space-y-7">
							{ADV_ITEM_KEYS.map((row) => (
								<li key={row.titleKey} className="flex gap-3 sm:gap-4">
									<CheckBullet />
									<div>
										<p className="mb-1 text-base font-semibold text-black sm:text-lg">{t(row.titleKey)}</p>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(row.descKey)}</p>
									</div>
								</li>
							))}
						</ul>
					</div>
				</div>

				{/* Applications：大屏左文右图；lg 双列 stretch，右图与左侧文案同高 */}
				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-12 xl:gap-16">
					<div className="flex min-w-0 flex-col lg:order-1 lg:w-1/2">
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("applicationsTitle")}
						</h2>
						<p className="mb-8 text-sm leading-relaxed text-black sm:mb-10 md:text-base">
							{t("applicationsIntro")}
						</p>
						<ul className="space-y-6 sm:space-y-7">
							{APP_ITEM_KEYS.map((row) => (
								<li key={row.titleKey} className="flex gap-3 sm:gap-4">
									<CheckBullet />
									<div>
										<p className="mb-1 text-base font-semibold text-black sm:text-lg">{t(row.titleKey)}</p>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(row.descKey)}</p>
									</div>
								</li>
							))}
						</ul>
					</div>
					<div className="relative w-full overflow-hidden lg:order-2 lg:min-h-0 lg:w-1/2">
						<div className="relative aspect-[4/3] w-full lg:absolute lg:inset-0 lg:aspect-auto">
							<Image
								src={applicationsImageSrc}
								alt={t("applicationsImageAlt")}
								fill
								className="object-fill"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
